import { useState, useEffect, useMemo } from 'react';
import { useAuthHeader } from 'react-auth-kit';
import { useColorModeValue } from '@chakra-ui/react';
import { getTransactionsAggregate } from 'api';

const CHART_OPTIONS = {
  chart: {
    toolbar: {
      show: false,
    },
  },
  dataLabels: {
    enabled: false,
  },
  fill: {
    type: 'gradient',
    gradient: {
      shade: 'light',
      type: 'vertical',
      shadeIntensity: 0.5,
      gradientToColors: undefined,
      inverseColors: true,
      opacityFrom: 0.8,
      opacityTo: 0,
      stops: [],
    },
  },
  grid: {
    strokeDashArray: 5,
  },
  legend: {
    show: false,
  },
  stroke: {
    curve: 'smooth',
  },
  tooltip: {
    theme: 'dark',
    x: {
      format: 'dd.MM.yy',
    },
    followCursor: true,
  },
  xaxis: {
    categories: [],
    labels: {
      style: {
        colors: '#000',
        fontSize: '12px',
      },
      hideOverlappingLabels: true,
    },
    tickPlacement: 'on',
    tooltip: {
      enabled: false,
    },
  },
  yaxis: {
    labels: {
      style: {
        colors: '#000',
        fontSize: '12px',
      },
    },
  },
};

function customChartTooltip({ w, dataPointIndex }) {
  function getTooltipContent() {
    let content = [];

    w.config.series.forEach((item, index) => {
      if (item.data[dataPointIndex]) {
        content.push({
          color: w.config.colors[index],
          name: item.name,
          data: item.data[dataPointIndex],
        });
      }
    });

    content.sort((a, b) => b.data - a.data);

    content = content.map(item => {
      return `
        <div class="apexcharts-tooltip-series-group apexcharts-active">
          <span
            class="apexcharts-tooltip-marker"
            style="background-color: ${item.color}"
          ></span>
          <div class="apexcharts-tooltip-text">
            <div class="apexcharts-tooltip-y-group">
              <span class="apexcharts-tooltip-text-y-label">
                ${item.name}:
              </span>
              <span class="apexcharts-tooltip-text-y-value">
                ${item.data}
              </span>
            </div>
          </div>
        </div>
      `;
    });

    return content.join('');
  }

  function getTooltipTitle() {
    return new Intl.DateTimeFormat('ru-RU', {
      dateStyle: 'short',
    }).format(w.config.xaxis.categories[dataPointIndex]);
  }

  return `
    <div class="apexcharts-tooltip-custom">
      <div class="apexcharts-tooltip-title">${getTooltipTitle()}</div>
      ${getTooltipContent()}
    </div>
  `;
}

export default function useChart({
  env,
  startedFrom,
  finishedTo,
  daysList,
  groupBy,
  categories,
  nameFormatter,
}) {
  const token = useAuthHeader()();
  const axisColor = useColorModeValue('#414040', '#f6f5f5');
  const [rawData, setRawData] = useState([]);
  const [ownerKey, typeKey] = groupBy.split(',');

  function getTimestamp(date) {
    return new Date(date).valueOf();
  }

  const colors = categories?.map(item => item.chartColor) || null;

  const chartOptions = useMemo(() => {
    const options = JSON.parse(JSON.stringify(CHART_OPTIONS));

    if (colors) {
      options.colors = colors;
      options.fill.colors = colors;
    }

    options.xaxis.categories = daysList;
    options.xaxis.type = 'datetime';
    options.xaxis.labels.datetimeFormatter = {
      year: 'yyyy',
      month: "MM 'yy",
      day: 'dd.MM.yy',
      hour: 'HH:mm',
    };
    options.xaxis.labels.style.colors = axisColor;
    options.yaxis.labels.style.colors = axisColor;
    options.tooltip.custom = customChartTooltip;

    return options;
  }, [colors, daysList, axisColor]);

  const chartData = useMemo(() => {
    const result = [];

    const daysMap = new Map(
      daysList.map(day => {
        return [day, {}];
      })
    );

    rawData.forEach(item => {
      const { count, started } = item;
      const ownerValue = item[ownerKey] ? item[ownerKey] : 'unknown';
      const typeValue = item[typeKey];
      const date = getTimestamp(started);

      if (
        categories.find(
          item => ownerValue === item[ownerKey] && typeValue === item.type
        )
      ) {
        const key = `${ownerValue}-${typeValue}`;

        if (daysMap.has(date)) {
          const value = daysMap.get(date);
          value[key] = count;

          daysMap.set(date, value);
        } else {
          daysMap.set(date, {
            [key]: count,
          });
        }
      }
    });

    categories.forEach(category => {
      const ownerValue = category[ownerKey];
      const typeValue = category[typeKey];

      const key = `${ownerValue}-${typeValue}`;

      const data = [];

      daysMap.forEach(value => {
        data.push(value[key] || 0);
      });

      const title = nameFormatter ? nameFormatter(category) : ownerValue;

      result.push({
        name: `${title} (${typeValue})`,
        data,
      });
    });

    return result;
  }, [categories, daysList, nameFormatter, ownerKey, rawData, typeKey]);

  useEffect(() => {
    async function fetchChartData(startedFrom, finishedTo) {
      const rawData = await getTransactionsAggregate({
        env,
        token,
        started: startedFrom,
        finished: finishedTo,
        groupBy,
      });

      setRawData(rawData);
    }

    fetchChartData(startedFrom, finishedTo);
  }, [token, startedFrom, finishedTo, groupBy, env]);

  return { chartOptions, chartData };
}
